import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DragDropPicker from '../../Utilities/DragDropPicker/DragDropPicker';
import { getPublicDownloadableLink } from '../../../../../Helper';
import { SwitchComponent } from '../../../../../Components';
import { UnitImagesTypes } from '../../Enums/UnitImagesTypes';
import { isEmpty } from 'lodash';
import { useKeyGenerator } from '../../../../../Hooks/keyGenerator';

const UnitImagesForm = ({
  localFormChangeHandler,
  localFormSelection,
  setDisabledPageToggle,
}) => {
  const parentTranslationPath = 'SmartProposal';
  const translationPath = 'UnitImagesForm.';
  const { t } = useTranslation(parentTranslationPath);
  const maxCheckedNo = 4;

  const [state, setState] = useState(null);
  const [key, refresh] = useKeyGenerator();
  const initializeState = () => {
    if (!state && localFormSelection?.data) setState({ ...localFormSelection });
  };

  const syncUnitImagesData = (selectedImages, imageType) => {
    let imagesData = [];

    if (imageType === UnitImagesTypes.Architecture)
      imagesData = state?.data?.propertyPublishedImages
        ? [...state?.data?.propertyPublishedImages]
        : [];
    else if (imageType === UnitImagesTypes.Interior)
      imagesData = state?.data?.unitPublishedImages
        ? [...state?.data?.unitPublishedImages]
        : [];

    let imagesDataClone = [...imagesData];

    imagesDataClone.forEach((item, index) => {
      const lastCheckedIndex = imagesData.reduce(
        (lastIndex, currentItem, idx) =>
          currentItem.isChecked ? idx : lastIndex,
        -1
      );
      const targetIndex = lastCheckedIndex + 1;
      const selectedImageIndex = index;

      const isImageSelected =
        selectedImages.findIndex((element) => element.id === item.fileId) !==
        -1;

      if (isImageSelected) {
        const checkedItem = {
          ...item,
          isChecked: true,
        };
        imagesData.splice(selectedImageIndex, 1);
        imagesData.splice(targetIndex, 0, checkedItem);
      }
    });

    return imagesData;
  };

  const unitPublishedImages = useMemo(() => {
    const selectedArchitectureImages = state?.architectureImages ?? [];
    const selectedInteriorImages = state?.interiorImages ?? [];
    // selectedArchitectureImages.forEach((item, index) => {
    //   if (index < 3) {
    //     item.isChecked = true;
    //   }
    // });
    // selectedInteriorImages.forEach((item, index) => {
    //   if (index < 3) {
    //     item.isChecked = true;
    //   }
    // });
    return {
      architectureImages: syncUnitImagesData(
        selectedArchitectureImages,
        UnitImagesTypes.Architecture
      ),
      interiorImages: syncUnitImagesData(
        selectedInteriorImages,
        UnitImagesTypes.Interior
      ),
    };
  }, [state?.interiorImages, state?.architectureImages]);
  const updateStateHandler = (unitImages, key) => {
    setState((prevState) => {
      let updatedState = { ...prevState, [key]: unitImages };

      switch (key) {
        case 'architectureImages': {
          updatedState.isSelectAllArchitecture =
            unitImages.length === maxCheckedNo;
          break;
        }
        case 'interiorImages': {
          updatedState.isSelectAllInterior = unitImages.length === maxCheckedNo;
          break;
        }
        default:
          break;
      }
      return updatedState;
    });
  };

  const unitImagesSelectHandler = useCallback(({ result, key }) => {
    const pickedImagesIds = Array.isArray(result)
      ? result.map((item) => item.fileId)
      : [];
    const unitImagesURLs = pickedImagesIds.map((id) =>
      id ? getPublicDownloadableLink(id) : null
    );

    const unitImages = pickedImagesIds.map((_, index) => ({
      url: unitImagesURLs[index],
      id: pickedImagesIds[index],
    }));

    updateStateHandler(unitImages, key);
  }, []);

  useEffect(() => {
    initializeState();
  }, [localFormSelection.data]);

  useEffect(() => {
    if (state) localFormChangeHandler('edit', { ...state });
  }, [state]);

  useEffect(() => {
    if (
      isEmpty(unitPublishedImages?.architectureImages) &&
      isEmpty(unitPublishedImages?.interiorImages) && state
    ) {
      setDisabledPageToggle(true);
      // state.showPage = false;
      setState((prevState) => ({
        ...prevState,
        showArchitectureImages: false,
        showInteriorImages: false,
        showPage: false,
      }));
    }
    if (
      isEmpty(unitPublishedImages?.architectureImages) &&
      state?.showArchitectureImages
    ) {
      setState((prevState) => ({
        ...prevState,
        showArchitectureImages: false,
      }));
    }
    if (
      isEmpty(unitPublishedImages?.interiorImages) &&
      state?.showInteriorImages
    ) {
      setState((prevState) => ({
        ...prevState,
        showInteriorImages: false,
      }));
    }
    return () => {
      refresh();
    };
  }, [
    unitPublishedImages.interiorImages,
    unitPublishedImages.architectureImages,
  ]);

  useEffect(() => {
    if (state?.data.propertyPublishedImages) {
      const selectedImages = [...state?.data.propertyPublishedImages].slice(
        0,
        3
      );
      unitImagesSelectHandler({
        result: selectedImages,
        key: 'architectureImages',
      });
    }
    if (state?.data.unitPublishedImages) {
      const selectedImages = [...state?.data.unitPublishedImages].slice(0, 3);
      unitImagesSelectHandler({
        result: selectedImages,
        key: 'interiorImages',
      });
    }
  }, [state?.data?.propertyPublishedImages, state?.data?.unitPublishedImages]);
  return (
    <>
      <div className='ProposalForm__field_box' key={key}>
        <div className='ProposalForm__field_label'>
          <p>Architecture images</p>
          <p>{`You can select up to ${maxCheckedNo}`}</p>
        </div>
        <div>
          <SwitchComponent
            key={key}
            idRef='showArchitectureImagesRef'
            wrapperClasses='mb-3'
            themeClass='thick-theme'
            isChecked={state?.showArchitectureImages}
            labelValue={state?.showArchitectureImages ? 'show' : 'hide'}
            parentTranslationPath={parentTranslationPath}
            isDisabled={isEmpty(unitPublishedImages?.architectureImages)}
            translationPath={''}
            onChangeHandler={(_, isChecked) =>
              setState((prevState) => ({
                ...prevState,
                showArchitectureImages: isChecked,
              }))
            }
          />
          <DragDropPicker
            onChange={(result) =>
              unitImagesSelectHandler({ result, key: 'architectureImages' })
            }
            itemsList={unitPublishedImages?.architectureImages}
            maxChecked={maxCheckedNo}
          />
        </div>
      </div>
      {localFormSelection?.data?.isSingleUnit && (
        <div className='ProposalForm__field_box'>
          <div className='ProposalForm__field_label'>
            <p>Interior images</p>
            <p>{`You can select up to ${maxCheckedNo}`}</p>
          </div>
          <div key={key}>
            <SwitchComponent
              key={key}
              idRef='showInteriorImagesRef'
              themeClass='thick-theme'
              wrapperClasses='mb-3'
              isChecked={state?.showInteriorImages}
              labelValue={state?.showInteriorImages ? 'show' : 'hide'}
              parentTranslationPath={parentTranslationPath}
              translationPath={''}
              isDisabled={isEmpty(unitPublishedImages?.interiorImages)}
              onChangeHandler={(_, isChecked) =>
                setState((prevState) => ({
                  ...prevState,
                  showInteriorImages: isChecked,
                }))
              }
            />
            <DragDropPicker
              onChange={(result) =>
                unitImagesSelectHandler({ result, key: 'interiorImages' })
              }
              itemsList={unitPublishedImages?.interiorImages}
              maxChecked={maxCheckedNo}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default UnitImagesForm;
